import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Mail, MessageCircle, Building2 } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — banana.cards" },
      { name: "description", content: "Talk to the banana.cards team about enterprise plans, partnerships or support." },
      { property: "og:title", content: "Contact banana.cards" },
      { property: "og:description", content: "We respond within 1 business day." },
    ],
    links: alternateLinkList("/contact", "en"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact banana.cards",
          description: "Talk to the banana.cards team about enterprise plans, partnerships or support.",
          url: "https://banana.cards/contact",
          mainEntity: {
            "@type": "Organization",
            name: "Banana Cards",
            url: "https://banana.cards",
            email: "hello@banana.cards",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Building A1, Dubai Digital Park, Dubai Silicon Oasis",
              addressLocality: "Dubai",
              addressCountry: "AE",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Contact</p>
              <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
                Let's <em className="italic">talk.</em>
              </h1>
              <p className="mt-6 max-w-md text-base text-muted-foreground md:text-lg">
                Whether you're rolling Banana out to 10 people or 10,000, our team is ready to help.
              </p>
              <div className="mt-10 space-y-4">
                {[
                  { icon: Building2, title: "Company", desc: "SIX STUDIO CREATIVE - FZCO" },
                  { icon: Mail, title: "General enquiries", desc: "hello@banana.cards" },
                  { icon: MessageCircle, title: "Support", desc: "24/7 chat from within the app." },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="flex items-start gap-4 rounded-2xl bg-muted p-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-card">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-sm font-bold">{c.title}</p>
                        <p className="text-sm text-muted-foreground">{c.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-2xl bg-muted p-5">
                <p className="text-sm font-bold">Office Address</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Building A1, Dubai Digital Park<br />
                  Dubai Silicon Oasis, Dubai<br />
                  United Arab Emirates
                </p>
              </div>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); alert("Thanks — we'll get back within 1 business day."); }}
              className="rounded-3xl bg-muted p-8"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name"><input required className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
                <Field label="Last name"><input required className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <div className="mt-4">
                <Field label="Work email"><input required type="email" className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Company"><input className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
                <Field label="Team size">
                  <select className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10">
                    <option>Just me</option><option>2–10</option><option>11–50</option><option>51–250</option><option>250+</option>
                  </select>
                </Field>
              </div>
              <div className="mt-4">
                <Field label="How can we help?"><textarea rows={4} className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <button className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Send message
              </button>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}